'use client';

import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Loader2, CalendarCheck, Sparkles, ShieldCheck, ArrowRight, Cpu, Users } from 'lucide-react';

// -----------------------------------------------------------------------------
// /rdv — Page publique de prise de RDV Découverte MASTRAT (hors auth)
//
// Promesse (chartée dans Notion) : "45 min gratuits, on repart avec 3 points
// concrets à améliorer même sans travailler avec nous".
// -----------------------------------------------------------------------------

const SEGMENTS = [
  'PME Services en Croissance',
  'TPE Multi-Activités',
  'Logistique / Distribution',
  'Commerce / Restauration',
  'Autre',
];

const SIZES = ['1-10', '10-50', '50-100', '100+'];

// Interlocuteurs : le visiteur choisit AVANT de voir les créneaux. Chacun a les
// siens (un RDV de l'un ne bloque pas l'autre). Libellés repris de mastrat.fr,
// page « Notre approche » ; mêmes clés que lib/agenda-link.ts côté serveur.
type HostKey = 'armando' | 'martin';

const HOSTS: Array<{ key: HostKey; name: string; firstName: string; role: string; topic: string; detail: string }> = [
  {
    key: 'armando',
    name: 'Armando Martins',
    firstName: 'Armando',
    role: "L'Opérateur Systèmes",
    topic: 'Le digital',
    detail: 'Outils, automatisation, intégrations, digital orienté résultat.',
  },
  {
    key: 'martin',
    name: 'Martin Robac',
    firstName: 'Martin',
    role: "L'Ingénieur Performance",
    topic: "L'humain",
    detail: "Structuration, lean, conduite du changement, dynamiques d'équipe.",
  },
];

function groupByDay(slots: string[]): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const s of slots) {
    const d = new Date(s);
    const key = d.toISOString().slice(0, 10);
    if (!map[key]) map[key] = [];
    map[key].push(s);
  }
  return map;
}

function labelDay(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

function labelTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}h${String(d.getMinutes()).padStart(2, '0')}`;
}

export default function RdvPage() {
  const [host, setHost] = useState<HostKey | null>(null);
  const [slots, setSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [pickedSlot, setPickedSlot] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ email: string; when: string; hostName: string; emailSent: boolean; meetingLink: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    sector: '',
    teamSize: '10-50',
    segment: 'PME Services en Croissance',
    summary: '',
    website: '', // honeypot
  });

  // Lien direct possible : /rdv?avec=armando ou /rdv?avec=martin
  useEffect(() => {
    const wanted = new URLSearchParams(window.location.search).get('avec');
    if (wanted === 'armando' || wanted === 'martin') setHost(wanted);
  }, []);

  // Les créneaux dépendent de l'interlocuteur : on les charge une fois le choix fait
  const loadSlots = async (who: HostKey) => {
    setLoadingSlots(true);
    try {
      const res = await fetch(`/api/public/rdv?with=${who}`, { cache: 'no-store' });
      const data = await res.json();
      setSlots(Array.isArray(data.slots) ? data.slots : []);
    } catch (err) {
      console.error(err);
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    setPickedSlot(null);
    setSlots([]);
    if (host) loadSlots(host);
  }, [host]);

  const hostInfo = HOSTS.find((h) => h.key === host) || null;

  const daysMap = useMemo(() => groupByDay(slots), [slots]);
  const dayKeys = useMemo(() => Object.keys(daysMap).slice(0, 10), [daysMap]);

  const canSubmit =
    form.firstName.trim() &&
    form.lastName.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
    form.company.trim() &&
    host &&
    pickedSlot;

  // Ce qui manque encore, affiché sous le bouton tant qu'il est gris
  const missing: string[] = [];
  if (!host) missing.push('ton interlocuteur');
  if (!pickedSlot) missing.push('un créneau');
  if (!form.firstName.trim()) missing.push('ton prénom');
  if (!form.lastName.trim()) missing.push('ton nom');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) missing.push('un email valide');
  if (!form.company.trim()) missing.push('le nom de ton entreprise');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch('/api/public/rdv', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          with: host,
          scheduledAt: pickedSlot,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.error || 'Une erreur est survenue, réessayez.');
        // Créneau pris entre-temps : on recharge la liste de cet interlocuteur
        if (res.status === 409 && host) {
          setPickedSlot(null);
          loadSlots(host);
        }
        return;
      }
      setSuccess({
        email: form.email,
        when: pickedSlot as string,
        hostName: hostInfo?.name || '',
        emailSent: data.emailSent === true,
        meetingLink: typeof data.meetingLink === 'string' ? data.meetingLink : '',
      });
    } catch (err) {
      console.error(err);
      setError('Erreur réseau — réessayez ou contactez armando@mastrat.fr');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0E1F44] to-[#0A162F] text-white flex flex-col items-center justify-center px-6 py-12">
        <div className="max-w-lg w-full bg-white rounded-2xl p-8 text-[#0E1F44] shadow-2xl">
          <div className="flex justify-center mb-4">
            <CheckCircle2 className="w-14 h-14 text-emerald-500" />
          </div>
          <h1 className="text-2xl font-bold text-center mb-3">RDV confirmé !</h1>
          {success.emailSent ? (
            <p className="text-slate-700 text-center leading-relaxed">
              Nous t&apos;avons envoyé un email de confirmation à <span className="font-semibold">{success.email}</span> avec le lien visio. Pense à regarder tes indésirables s&apos;il n&apos;arrive pas.
            </p>
          ) : (
            <p className="text-slate-700 text-center leading-relaxed">
              Ton créneau est bien réservé. Note-le dès maintenant, avec le lien visio ci-dessous.
            </p>
          )}
          <div className="bg-[#C9A961]/10 border border-[#C9A961]/40 rounded-lg p-4 mt-5">
            <p className="text-sm font-semibold text-[#0E1F44] mb-1">
              Ton créneau{success.hostName ? ` avec ${success.hostName}` : ''}
            </p>
            <p className="text-lg font-bold">
              {new Date(success.when).toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
            <p className="text-lg font-bold">à {labelTime(success.when)}</p>
            {success.meetingLink && (
              <p className="mt-2 text-sm break-all">
                Lien visio : <a href={success.meetingLink} className="font-semibold underline">{success.meetingLink}</a>
              </p>
            )}
          </div>
          <div className="mt-6 text-sm text-slate-600 space-y-2">
            <p>
              <span className="font-semibold text-[#0E1F44]">Ce qu&apos;on prépare pour toi :</span> une lecture rapide de tes signaux public (site, réseaux) pour orienter la discussion.
            </p>
            <p>
              <span className="font-semibold text-[#0E1F44]">Ce qu&apos;on te promet :</span> 3 points concrets à améliorer dans ton organisation, que tu bosses avec nous ou pas.
            </p>
          </div>
          <a
            href="https://mastrat.fr"
            className="mt-8 block text-center text-sm text-slate-500 hover:text-[#0E1F44]"
          >
            Retour au site MASTRAT
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header signature */}
      <div className="bg-gradient-to-r from-[#0E1F44] to-[#0A162F] text-white">
        <div className="max-w-4xl mx-auto px-6 pt-10 pb-14">
          <div className="flex items-center gap-2 text-[#C9A961] text-sm font-semibold uppercase tracking-widest">
            <Sparkles className="w-4 h-4" />
            MASTRAT — RDV Découverte
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mt-3 leading-tight">
            45 minutes offertes, <br className="hidden md:block" />
            <span className="text-[#C9A961]">3 points concrets à améliorer.</span>
          </h1>
          <p className="mt-4 text-slate-200 max-w-2xl text-lg">
            On échange sur ton contexte, on te dit ce qu&apos;on voit — que tu travailles avec nous ou pas. Zéro engagement, zéro pitch commercial.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-8 text-sm">
            <div className="flex items-start gap-2 bg-white/5 rounded-lg p-3">
              <CalendarCheck className="w-5 h-5 text-[#C9A961] flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Visio 45 min</div>
                <div className="text-slate-300 text-xs">Lien envoyé par mail dès réservation</div>
              </div>
            </div>
            <div className="flex items-start gap-2 bg-white/5 rounded-lg p-3">
              <Sparkles className="w-5 h-5 text-[#C9A961] flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Synthèse actionnable</div>
                <div className="text-slate-300 text-xs">3 points concrets à mettre en place la semaine suivante</div>
              </div>
            </div>
            <div className="flex items-start gap-2 bg-white/5 rounded-lg p-3">
              <ShieldCheck className="w-5 h-5 text-[#C9A961] flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Diagnostic Flash</div>
                <div className="text-slate-300 text-xs">Déductible si tu signes une mission sous 30 jours</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 -mt-8 pb-16">
        <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 md:p-8 space-y-8">
          {/* Étape 1 : Choix de l'interlocuteur */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 rounded-full bg-[#0E1F44] text-white flex items-center justify-center font-bold text-sm">
                1
              </span>
              <h2 className="text-xl font-bold text-[#0E1F44]">Avec qui veux-tu échanger ?</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" role="radiogroup" aria-label="Interlocuteur">
              {HOSTS.map((h) => {
                const active = host === h.key;
                const Icon = h.key === 'armando' ? Cpu : Users;
                return (
                  <button
                    key={h.key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    data-host={h.key}
                    onClick={() => setHost(h.key)}
                    className={
                      active
                        ? 'text-left rounded-xl border-2 border-[#0E1F44] bg-[#0E1F44] text-white p-4 transition'
                        : 'text-left rounded-xl border-2 border-slate-200 bg-white text-slate-900 hover:border-[#C9A961] p-4 transition'
                    }
                  >
                    <div className="flex items-start gap-3">
                      <Icon className={active ? 'w-6 h-6 text-[#C9A961] flex-shrink-0 mt-0.5' : 'w-6 h-6 text-[#0E1F44] flex-shrink-0 mt-0.5'} />
                      <div className="min-w-0">
                        <div className={active ? 'text-xs font-semibold uppercase tracking-wider text-[#C9A961]' : 'text-xs font-semibold uppercase tracking-wider text-[#8A6F2E]'}>
                          {h.topic}
                        </div>
                        <div className="text-lg font-bold leading-tight mt-0.5">{h.name}</div>
                        <div className={active ? 'text-sm text-slate-200' : 'text-sm text-slate-600'}>{h.role}</div>
                        <div className={active ? 'text-sm text-slate-300 mt-2' : 'text-sm text-slate-500 mt-2'}>{h.detail}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Étape 2 : Choix du créneau */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 rounded-full bg-[#0E1F44] text-white flex items-center justify-center font-bold text-sm">
                2
              </span>
              <h2 className="text-xl font-bold text-[#0E1F44]">
                {hostInfo ? `Choisis ton créneau avec ${hostInfo.firstName}` : 'Choisis ton créneau'}
              </h2>
            </div>

            {!hostInfo ? (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600">
                Choisis d&apos;abord ton interlocuteur : chacun a ses propres créneaux.
              </div>
            ) : loadingSlots ? (
              <div className="flex items-center gap-2 text-slate-500 py-6">
                <Loader2 className="w-4 h-4 animate-spin" />
                Chargement des créneaux…
              </div>
            ) : dayKeys.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
                Plus de créneaux disponibles pour l&apos;instant — écris-nous à <a href="mailto:armando@mastrat.fr" className="underline font-semibold">armando@mastrat.fr</a> et on te propose un moment sous 24h.
              </div>
            ) : (
              <div className="space-y-3">
                {dayKeys.map((day) => (
                  <div key={day}>
                    <div className="text-sm font-semibold text-slate-700 mb-1.5 first-letter:uppercase">
                      {labelDay(day)}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {daysMap[day].map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setPickedSlot(slot)}
                          className={
                            pickedSlot === slot
                              ? 'bg-[#0E1F44] text-white border-[#0E1F44] px-4 py-2 rounded-lg text-sm font-semibold border transition'
                              : 'bg-white border-slate-300 text-slate-800 hover:border-[#C9A961] hover:text-[#0E1F44] px-4 py-2 rounded-lg text-sm font-semibold border transition'
                          }
                        >
                          {labelTime(slot)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Étape 3 : Coordonnées */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 rounded-full bg-[#0E1F44] text-white flex items-center justify-center font-bold text-sm">
                3
              </span>
              <h2 className="text-xl font-bold text-[#0E1F44]">Tes coordonnées</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="f-app-rdv-page-1" className="block text-sm font-medium text-slate-700 mb-1">Prénom *</label>
                <input id="f-app-rdv-page-1"
                  type="text"
                  required
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-[#C9A961]/50 focus:border-[#C9A961]"
                />
              </div>
              <div>
                <label htmlFor="f-app-rdv-page-2" className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
                <input id="f-app-rdv-page-2"
                  type="text"
                  required
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-[#C9A961]/50 focus:border-[#C9A961]"
                />
              </div>
              <div>
                <label htmlFor="f-app-rdv-page-3" className="block text-sm font-medium text-slate-700 mb-1">Email pro *</label>
                <input id="f-app-rdv-page-3"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-[#C9A961]/50 focus:border-[#C9A961]"
                />
              </div>
              <div>
                <label htmlFor="f-app-rdv-page-4" className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
                <input id="f-app-rdv-page-4"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-[#C9A961]/50 focus:border-[#C9A961]"
                />
              </div>
            </div>

            {/* Honeypot */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              autoComplete="off"
              tabIndex={-1}
              className="absolute left-[-9999px]"
              aria-hidden="true"
            />
          </section>

          {/* Étape 4 : Contexte entreprise */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-8 h-8 rounded-full bg-[#0E1F44] text-white flex items-center justify-center font-bold text-sm">
                4
              </span>
              <h2 className="text-xl font-bold text-[#0E1F44]">Ton entreprise</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="f-app-rdv-page-5" className="block text-sm font-medium text-slate-700 mb-1">Nom de l&apos;entreprise *</label>
                <input id="f-app-rdv-page-5"
                  type="text"
                  required
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-[#C9A961]/50 focus:border-[#C9A961]"
                />
              </div>
              <div>
                <label htmlFor="f-app-rdv-page-6" className="block text-sm font-medium text-slate-700 mb-1">Secteur d&apos;activité</label>
                <input id="f-app-rdv-page-6"
                  type="text"
                  value={form.sector}
                  onChange={(e) => setForm({ ...form, sector: e.target.value })}
                  placeholder="Ex : logistique, restauration…"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-[#C9A961]/50 focus:border-[#C9A961]"
                />
              </div>
              <div>
                <label htmlFor="f-app-rdv-page-7" className="block text-sm font-medium text-slate-700 mb-1">Taille équipe</label>
                <select id="f-app-rdv-page-7"
                  value={form.teamSize}
                  onChange={(e) => setForm({ ...form, teamSize: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-base sm:text-sm bg-white"
                >
                  {SIZES.map((s) => (
                    <option key={s} value={s}>
                      {s} personnes
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="f-app-rdv-page-8" className="block text-sm font-medium text-slate-700 mb-1">Segment</label>
                <select id="f-app-rdv-page-8"
                  value={form.segment}
                  onChange={(e) => setForm({ ...form, segment: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-base sm:text-sm bg-white"
                >
                  {SEGMENTS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="f-app-rdv-page-9" className="block text-sm font-medium text-slate-700 mb-1">
                En 2-3 phrases, qu&apos;est-ce qui te frotte le plus dans ton organisation aujourd&apos;hui ? (optionnel mais recommandé)
              </label>
              <textarea id="f-app-rdv-page-9"
                rows={3}
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                placeholder="Ex : je passe mes journées à gérer des trucs qui devraient rouler tout seul, mes équipes viennent me voir pour tout, je perds du temps sur des outils qu&apos;on n&apos;utilise pas correctement…"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-base sm:text-sm focus:ring-2 focus:ring-[#C9A961]/50 focus:border-[#C9A961]"
              />
              <p className="text-xs text-slate-500 mt-1">
                On lit toujours ce que tu écris avant le RDV — plus tu es précis, plus le RDV est utile.
              </p>
            </div>
          </section>

          {/* Erreur */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-900">{error}</div>
          )}

          {/* CTA */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-100">
            <div>
              <p className="text-xs text-slate-500">
                En envoyant ce formulaire, tu acceptes qu&apos;on te recontacte pour ce RDV. Aucune donnée revendue.
              </p>
              {missing.length > 0 && (
                <p className="mt-1 text-xs font-medium text-[#0E1F44]" role="status">
                  Il manque : {missing.join(', ')}.
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className={
                canSubmit && !submitting
                  ? 'flex items-center gap-2 bg-[#C9A961] hover:bg-[#B08F4E] text-[#0E1F44] font-bold px-6 py-3 rounded-lg transition shadow-lg shadow-[#C9A961]/30'
                  : 'flex items-center gap-2 bg-slate-200 text-slate-500 font-bold px-6 py-3 rounded-lg cursor-not-allowed'
              }
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Envoi…
                </>
              ) : (
                <>
                  Réserver mon RDV
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </form>

        <p className="text-center text-xs text-slate-500 mt-6">
          Un problème ? Écris directement à <a href="mailto:armando@mastrat.fr" className="text-[#0E1F44] font-semibold underline">armando@mastrat.fr</a>.
        </p>
      </div>
    </div>
  );
}
